import { getBaseURL } from '@scoutus/sports-metrics';
import { Config } from '../Config';

/**
 * Origin the admin app should use for public `/api/v1/media/:id/url` redirects.
 * Prefer runtime Config.API_BASE_URL (local: localhost:3000) over whatever host
 * the API embedded via AUTH0_AUDIENCE.
 */
function mediaApiOrigin(): string {
  const raw =
    (typeof Config.API_BASE_URL === 'string' && Config.API_BASE_URL.trim()) ||
    getBaseURL() ||
    '';
  const trimmed = raw.replace(/\/$/, '');
  if (!trimmed) return '';
  try {
    return new URL(trimmed).origin;
  } catch {
    return trimmed;
  }
}

function mediaIdFromApiUrl(value: string): { id: string; kind: 'url' | 'thumbUrl' } | null {
  const match = value.match(
    /(?:^|\/)api\/v1\/media\/(\d+)\/(url|thumbUrl)(?:\?|$)/i,
  );
  if (!match?.[1] || !match[2]) return null;
  const kind = match[2].toLowerCase() === 'thumburl' ? 'thumbUrl' : 'url';
  return { id: match[1], kind };
}

function mediaIdFromStoragePath(value: string): string | null {
  const match = value.match(/(?:^|\/)media\/(\d+)-/);
  return match?.[1] ?? null;
}

/**
 * Turns an API-built media redirect, bare storage path, or absolute URL into an
 * `<img src>` that hits the admin's configured API (so local DB media works).
 */
export function resolveMediaDisplayUrl(
  value: string | null | undefined,
): string | null {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (!trimmed) return null;

  const origin = mediaApiOrigin();
  const fromApi = mediaIdFromApiUrl(trimmed);
  if (fromApi) {
    return origin
      ? `${origin}/api/v1/media/${fromApi.id}/${fromApi.kind}`
      : `/api/v1/media/${fromApi.id}/${fromApi.kind}`;
  }

  const fromPath = mediaIdFromStoragePath(trimmed);
  if (fromPath) {
    return origin
      ? `${origin}/api/v1/media/${fromPath}/url`
      : `/api/v1/media/${fromPath}/url`;
  }

  if (trimmed.startsWith('/api/v1/media/')) {
    return origin ? `${origin}${trimmed}` : trimmed;
  }

  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('//')) {
    return trimmed;
  }

  return trimmed;
}
